/**
 * The human check (ADR-123): Cloudflare Turnstile, exchanged for a pass.
 *
 * A Turnstile widget admits ten hostnames and this widget runs on every
 * client's website, so the challenge cannot run in the page. It runs in a page
 * of the backend's (the stream host's `/human-check`), in an iframe this
 * widget mounts:
 *
 *   frame ── Turnstile ──► postMessage {type: 'token'}
 *   POST {apiUrl}/chat/human-check {tenantId, token} ──► {pass, expiresAt}
 *   every turn and lead carries `humanPass`
 *
 * The server says whether to run it, and how: `humanCheck` on GET /chat/config,
 * and again inside a `human_check_required` refusal, so a widget mounted
 * without the config (a console preview) still learns it the first time a
 * turn is refused.
 *
 *   log       run it, attach the pass when there is one, never wait for it
 *   enforce   a turn waits for a pass; a refusal re-runs the check and the
 *             turn is sent again once (a refusal is never admitted or metered)
 *
 * Pure apart from `exchangeToken` and the storage helpers, so
 * scripts/test-config.mjs can pin it. The React half is HumanCheckFrame.tsx.
 */

export type HumanCheckMode = 'log' | 'enforce';

export interface HumanCheckConfig {
  mode: HumanCheckMode;
  siteKey: string;
  /** The challenge page, absolute https. Its origin is the only one whose messages are read. */
  pageUrl: string;
}

/** The refusal code a turn or a lead comes back with when its pass is missing or stale. */
export const HUMAN_CHECK_REQUIRED = 'human_check_required';

/** The `source` every message from the challenge page carries. */
export const FRAME_SOURCE = 'pwe-human-check';

/** A pass this close to its expiry is not attached: it could lapse on the way. */
export const PASS_MARGIN_MS = 60_000;

const SITE_KEY_RE = /^[A-Za-z0-9_-]{1,64}$/;

/** Shape check for the server's `humanCheck`; null for anything else, including null. */
export function parseHumanCheck(raw: unknown): HumanCheckConfig | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const mode = o.mode === 'log' || o.mode === 'enforce' ? o.mode : null;
  const siteKey = typeof o.siteKey === 'string' && SITE_KEY_RE.test(o.siteKey) ? o.siteKey : '';
  let pageUrl = '';
  if (typeof o.pageUrl === 'string') {
    try {
      const url = new URL(o.pageUrl);
      if (url.protocol === 'https:' && !url.hash) pageUrl = url.toString();
    } catch {
      pageUrl = '';
    }
  }
  return mode && siteKey && pageUrl ? { mode, siteKey, pageUrl } : null;
}

/** A 403 body that asks for the check. */
export function isHumanCheckRefusal(status: number, body: unknown): boolean {
  return (
    status === 403 &&
    !!body &&
    typeof body === 'object' &&
    (body as Record<string, unknown>).code === HUMAN_CHECK_REQUIRED
  );
}

/**
 * The frame's address. Everything the page needs is in the FRAGMENT, which a
 * browser never sends to a server: the site key, the language, the theme, and
 * this page's origin — the one origin the challenge page will post the token to.
 */
export function challengeUrl(
  config: HumanCheckConfig,
  opts: { origin: string; language: string; theme?: 'light' | 'dark' | 'auto' },
): string {
  const base = (opts.language || '').split('-')[0].toLowerCase();
  const params = new URLSearchParams({
    k: config.siteKey,
    o: opts.origin,
    l: /^[a-z]{2,3}$/.test(base) ? base : 'auto',
    t: opts.theme ?? 'auto',
  });
  return `${config.pageUrl}#${params.toString()}`;
}

export function frameOrigin(config: HumanCheckConfig): string {
  return new URL(config.pageUrl).origin;
}

export type FrameMessage =
  | { type: 'ready' }
  | { type: 'rendered' }
  | { type: 'token'; token: string }
  | { type: 'interactive' }
  | { type: 'settled' }
  | { type: 'expired' }
  | { type: 'error'; code: string };

/** A message from the challenge page, or null for anything else posted to this window. */
export function parseFrameMessage(data: unknown): FrameMessage | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const o = data as Record<string, unknown>;
  if (o.source !== FRAME_SOURCE) return null;
  switch (o.type) {
    case 'ready':
    case 'rendered':
    case 'interactive':
    case 'settled':
    case 'expired':
      return { type: o.type };
    case 'token':
      return typeof o.token === 'string' && o.token.length > 0 && o.token.length <= 2048
        ? { type: 'token', token: o.token }
        : null;
    case 'error':
      return { type: 'error', code: typeof o.code === 'string' ? o.code.slice(0, 64) : '' };
    default:
      return null;
  }
}

/**
 * Turnstile's own error codes that no retry fixes: an invalid site key
 * (110100, 110110, 400020), a hostname not on the widget (110200), an invalid
 * action or cData (110420, 110430), an unsupported browser (110500), or the
 * page's own checks. Everything else — 1106xx is a challenge that timed out —
 * is left to Turnstile's automatic retry.
 */
const TERMINAL_FRAME_ERRORS = new Set(['110100', '110110', '110200', '110420', '110430', '110500', '400020', 'bad-site-key', 'unsupported']);

export function isTerminalFrameError(code: string): boolean {
  return TERMINAL_FRAME_ERRORS.has(code);
}

export interface StoredPass {
  pass: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

export function isUsablePass(stored: StoredPass | null | undefined, now = Date.now()): stored is StoredPass {
  return !!stored && typeof stored.pass === 'string' && stored.pass.length > 0 && stored.expiresAt - PASS_MARGIN_MS > now;
}

export type ExchangeOutcome =
  | { kind: 'ok'; pass: StoredPass }
  /** Cloudflare said no: run the challenge again, never resend the token. */
  | { kind: 'failed' }
  /** The same refusals a turn gets: this site, or this assistant, is not served. */
  | { kind: 'blocked'; code: 'origin_not_allowed' | 'assistant_unavailable' | 'tenant_unknown' }
  /** Network, timeout, 5xx, a route not deployed, the check switched off. */
  | { kind: 'retry'; code: string };

export async function exchangeToken(
  apiUrl: string,
  tenantId: string,
  token: string,
  timeoutMs = 15_000,
): Promise<ExchangeOutcome> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${apiUrl}/chat/human-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, token }),
      signal: ctrl.signal,
    });
    const body = await res.json().catch(() => null);
    const code = body && typeof body === 'object' && typeof body.code === 'string' ? body.code : '';
    if (res.ok) {
      const pass = body && typeof body.pass === 'string' ? body.pass : '';
      // From `expiresIn`, counted from now on THIS clock: the server's
      // `expiresAt` compared with a browser clock that runs fast would make
      // every pass look spent, and every turn run a fresh challenge.
      const expiresIn = body && typeof body.expiresIn === 'number' && body.expiresIn > 0 ? body.expiresIn : 0;
      return pass && expiresIn
        ? { kind: 'ok', pass: { pass, expiresAt: Date.now() + expiresIn * 1000 } }
        : { kind: 'retry', code: 'malformed' };
    }
    if (code === 'human_check_failed') return { kind: 'failed' };
    if (code === 'origin_not_allowed' || code === 'assistant_unavailable' || code === 'tenant_unknown') {
      return { kind: 'blocked', code };
    }
    return { kind: 'retry', code: code || `http_${res.status}` };
  } catch {
    return { kind: 'retry', code: ctrl.signal.aborted ? 'timeout' : 'network' };
  } finally {
    clearTimeout(timer);
  }
}

// ── The pass, for the rest of the browser session ───────────────
// sessionStorage, not IndexedDB with the conversation: a pass is a bearer
// token for half an hour and has no business outliving the tab. Every access
// is guarded — storage can be absent, full or blocked (a sandboxed frame, a
// private window), and then the pass simply lives in memory.

export function passKey(tenantId: string): string {
  return `pweb-chat-pass:${tenantId}`;
}

export function loadPass(tenantId: string): StoredPass | null {
  try {
    const raw = sessionStorage.getItem(passKey(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPass;
    return isUsablePass(parsed) ? { pass: parsed.pass, expiresAt: parsed.expiresAt } : null;
  } catch {
    return null;
  }
}

export function savePass(tenantId: string, stored: StoredPass): void {
  try {
    sessionStorage.setItem(passKey(tenantId), JSON.stringify(stored));
  } catch {
    // In memory only.
  }
}

export function clearPass(tenantId: string): void {
  try {
    sessionStorage.removeItem(passKey(tenantId));
  } catch {
    // Nothing stored.
  }
}
